import posthog, {
  type CapturedNetworkRequest,
  type CaptureResult,
} from 'posthog-js';

import {
  POSTHOG_KEY,
  POSTHOG_MOKOBOKO_KEY,
  POSTHOG_HOST,
  APP_NAME,
  PLATFORM_DOMAIN,
} from './env';
import { IGNORED_ERROR_CODES } from './constants';
import { holdsGiftPagePath, isGiftPagePath } from './gift-page';
import { isStandaloneDisplay } from './install-platform';
import { postHogKeyForHost } from './posthog-project';
import { isUnsubscribePagePath } from './unsubscribe-page';

const PROJECT_KEY =
  typeof window === 'undefined'
    ? POSTHOG_KEY
    : postHogKeyForHost({
        host: window.location.hostname,
        platformDomain: PLATFORM_DOMAIN,
        defaultKey: POSTHOG_KEY,
        mokobokoKey: POSTHOG_MOKOBOKO_KEY,
      });

export function isPostHogEnabled(): boolean {
  // The admin's appearance preview iframe must not start session recording
  // or capture events; init below runs at module scope, so gate it here.
  // Exact, not a prefix: a top-up may legally hold the slug 'preview-pass',
  // and it is served at the site root. The gift recipient page and the
  // unsubscribe page stay dark for the life of a document loaded there: the
  // one's path is a gift card code, the other's the link's token, and
  // global-error brings this module onto every page.
  if (
    typeof window !== 'undefined' &&
    (window.location.pathname === '/preview' ||
      isGiftPagePath(window.location.pathname) ||
      isUnsubscribePagePath(window.location.pathname))
  ) {
    return false;
  }
  return !!PROJECT_KEY;
}

// Every studio but MokoBoko shares the platform's PostHog project (see
// posthog-project.ts), so the slug super property + group is what keeps them
// distinguishable. Must be re-run after every
// posthog.reset(), which wipes super properties and group associations, and it
// owns every super property rather than just the tenant one so a partial
// restore cannot silently drop the ones a caller forgot.
//
// The slug comes from the resolved tenant record, not the hostname: a studio on
// its own domain has no slug in its address, so deriving it there would file
// that studio's events under a group that does not exist.
export function registerSuperProperties(tenantSlug?: string) {
  posthog.register({
    app_name: APP_NAME,
    // Whether this visit was launched from the Home Screen. The manifest's
    // start_url carries no marker, so this is the only way to tell.
    display_mode: isStandaloneDisplay() ? 'standalone' : 'browser',
    ...(tenantSlug && { tenant_slug: tenantSlug }),
  });
  if (tenantSlug) posthog.group('tenant', tenantSlug);
}

// Replays show names and email addresses wherever they are typed, and every
// field of the checkout (its page carries data-ph-unmask). Passwords, sign-in
// codes and gift card codes stay masked everywhere: a secret field outranks
// any ancestor that unmasks. A mask function rather than maskAllInputs: false,
// because the project's replay masking setting overrides that flag and nothing
// overrides this.
const SHOWN_AUTOCOMPLETE = new Set(['given-name', 'family-name', 'email']);
const SHOWN_FIELD = /(^|\.)(first_name|last_name|email)$/;
const SECRET_AUTOCOMPLETE = new Set([
  'current-password',
  'new-password',
  'one-time-code',
]);
const SECRET_FIELD = '[data-ph-secret], .ph-no-capture';

// helpers/gift-card.ts's LOOSE_GIFT_CODE, the backend's log mask, copied so
// this module, which every page loads, does not pull in the checkout helpers.
// A code is cash: one typed or pasted into any field is masked.
const GIFT_CODE =
  /G[\s\p{Cf}\p{Pd}−]*F[\s\p{Cf}\p{Pd}−]*T(?:[\s\p{Cf}\p{Pd}−]*[A-Z0-9]){12}/giu;

const holdsGiftCode = (text: string) => text.search(GIFT_CODE) !== -1;

const isSecretField = (field: HTMLElement) =>
  field.getAttribute('type') === 'password' ||
  // rrweb's mark on a password field whose show toggle made it type="text".
  field.hasAttribute('data-rr-is-password') ||
  SECRET_AUTOCOMPLETE.has(field.getAttribute('autocomplete') ?? '') ||
  !!field.closest(SECRET_FIELD);

const isShownField = (field: HTMLElement) =>
  !!field.closest('[data-ph-unmask]') ||
  SHOWN_AUTOCOMPLETE.has(field.getAttribute('autocomplete') ?? '') ||
  SHOWN_FIELD.test(field.getAttribute('name') ?? '');

export const maskReplayInput = (text: string, element?: HTMLElement) =>
  element &&
  !isSecretField(element) &&
  isShownField(element) &&
  !holdsGiftCode(text)
    ? text
    : '*'.repeat(text.length);

// A captured request keeps its URL, and its bodies when the project records
// them: the discount check carries its code in the query string, every gift
// card route carries the code under gift_card_code, a gift card purchase
// carries the giftee's name and address and the buyer's message under
// recipient (the backend logger's own key, D22, G70), and a gift receipt read
// sends the receipt token that shows a send-to-self code beside the session id.
// Every gift key a booking site route sends or reads in a body is listed here.
const SECRET_KEYS = new Set(['coupon_code', 'gift_card_code', 'recipient']);
const SECRET_PARAM =
  /([?&]|^)(coupon_code|gift_card_code|receipt_token)=[^&#]*/g;
const REDACTED = '[REDACTED]';

// posthog-js's own body scrub (its lazy recorder's default maskRequestFn),
// which it stops running once a mask function is given: a body holding a card
// number, an SSN or one of these words is dropped whole.
const CARD_NUMBER =
  /(4[0-9]{12}(?:[0-9]{3})?)|(5[1-5][0-9]{14})|(6(?:011|5[0-9]{2})[0-9]{12})|(3[47][0-9]{13})|(3(?:0[0-5]|[68][0-9])[0-9]{11})|((?:2131|1800|35[0-9]{3})[0-9]{11})/;
const SSN = /\d{3}-?\d{2}-?\d{4}/;
const SECRET_BODY_WORDS = [
  'password',
  'secret',
  'passwd',
  'api_key',
  'apikey',
  'auth',
  'credentials',
  'mysql_pwd',
  'privatekey',
  'private_key',
  'token',
];

const isDroppedBody = (body: string) =>
  CARD_NUMBER.test(body.replace(/[- ]/g, '')) ||
  SSN.test(body) ||
  SECRET_BODY_WORDS.some((word) => body.includes(word));

const maskSecretText = (text: string) =>
  text.replace(SECRET_PARAM, `$1$2=${REDACTED}`).replace(GIFT_CODE, REDACTED);

const redactJson = (value: unknown): unknown => {
  if (typeof value === 'string') return value.replace(GIFT_CODE, REDACTED);
  if (Array.isArray(value)) return value.map(redactJson);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => [
      key,
      SECRET_KEYS.has(key) && inner != null ? REDACTED : redactJson(inner),
    ])
  );
};

const maskBody = (body: string) => {
  if (isDroppedBody(body)) return REDACTED;
  try {
    return JSON.stringify(redactJson(JSON.parse(body)));
  } catch {
    return maskSecretText(body);
  }
};

export const maskReplayNetworkRequest = (
  request: CapturedNetworkRequest
): CapturedNetworkRequest => ({
  ...request,
  name: maskSecretText(request.name),
  requestBody: request.requestBody && maskBody(request.requestBody),
  responseBody: request.responseBody && maskBody(request.responseBody),
});

// Extension and in-app-browser content scripts share the page's window, and
// window.onerror is per-realm rather than per-origin, so posthog-js reports
// their crashes as ours. Their frames carry no filename — injected inline, so
// no script URL — or an extension scheme; ours always resolve to a chunk under
// /_next/ on the current origin.
const isAppFrame = (frame: { filename?: string }) =>
  !!frame.filename &&
  frame.filename.startsWith(`${window.location.origin}/_next/`);

// A frameless exception is kept: posthog-js substitutes its own synthetic stack
// when the browser withholds the real one, which means the origin is unknown,
// not that the code was foreign.
const isThirdPartyException = (exception: {
  stacktrace?: { frames?: { filename?: string }[] };
}) => {
  const frames = exception?.stacktrace?.frames;
  return !!frames?.length && !frames.some(isAppFrame);
};

if (typeof window !== 'undefined' && isPostHogEnabled()) {
  const isDevelopment = process.env.NODE_ENV === 'development';

  posthog.init(PROJECT_KEY!, {
    // Dev: direct connection (server may not have internet access locally).
    // Prod: proxy through /ingest to bypass ad blockers.
    api_host: POSTHOG_HOST,
    ui_host: 'https://eu.posthog.com',
    capture_pageview: false,
    capture_exceptions: true,
    session_recording: {
      maskAllInputs: true,
      maskInputFn: maskReplayInput,
      maskCapturedNetworkRequestFn: maskReplayNetworkRequest,
    },
    // A host page can carry several embeds, each its own JS context, so leaving
    // this on would start one recorder per iframe for a single visitor.
    disable_session_recording: window.self !== window.top,
    // No tenant slug yet — the record has not been fetched at init, so this is
    // re-run with it once it resolves.
    loaded: () => registerSuperProperties(),
    // Enrich auto-captured $exception events (via capture_exceptions: true)
    // with the same custom properties that captureError() adds, so Slack
    // template variables are populated for ALL errors.
    before_send: (event: CaptureResult | null) => {
      // A document that loaded elsewhere is already recording when it reaches
      // the gift or unsubscribe page in-app, and a replay batch carries no URL
      // of its own, so the page the tab is on now counts as well as the
      // event's own.
      if (
        !event ||
        isGiftPagePath(window.location.pathname) ||
        isUnsubscribePagePath(window.location.pathname) ||
        holdsGiftPagePath(event.properties, window.location.origin)
      ) {
        return null;
      }
      if (event.event !== '$exception') return event;

      // Drop auth noise. An auth-endpoint failure that carries no backend code
      // — a refresh 401, a network fault on the session check — is named
      // AuthError by toHandledError; one that carries a code keeps it and is
      // dropped through IGNORED_ERROR_CODES below (LOGIN_REQUIRED, the tenant
      // codes).
      const exList = event.properties?.$exception_list;
      const firstType = Array.isArray(exList) ? exList[0]?.type : null;
      if (
        firstType === 'AuthError' ||
        firstType === 'ChunkLoadError' ||
        (firstType && IGNORED_ERROR_CODES.includes(firstType))
      ) {
        return null;
      }

      // Drop SecurityErrors from PostHog's own session recorder trying to access
      // cross-origin Window properties (rrweb iframe instrumentation).
      const firstValue = Array.isArray(exList) ? exList[0]?.value : null;
      if (
        firstType === 'SecurityError' &&
        typeof firstValue === 'string' &&
        firstValue.includes('removeEventListener')
      ) {
        return null;
      }

      // Ahead of the enrichment below, so a foreign crash is never stamped with
      // this app's name and route and alerted on as one of ours.
      if (
        Array.isArray(exList) &&
        exList.length > 0 &&
        exList.every(isThirdPartyException)
      ) {
        return null;
      }

      // Already enriched by captureError() via captureException — don't overwrite
      if (event.properties?.error_source) return event;

      event.properties = {
        ...event.properties,
        app_name: APP_NAME,
        error_source: 'unhandled',
        error_severity: 'error',
        current_route: window.location.pathname,
        current_page: window.location.href,
        exception_timestamp: new Date().toISOString(),
      };

      return event;
    },
  });
}

export { posthog };
